import { CpuChipIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import Tag from 'components/common/tag';
import Select from 'components/inputs/select';
import ProductLink from 'components/products/product-link';
import ProductPhoto from 'components/products/product-photo';
import { AccessoryProductType, SystemOptionCondenserType } from 'data/types';
import { useEffect, useRef, useState } from 'react';
import {
  EquipmentAccessoryData,
  LineSetSettings
} from './accessory-type-panel';

const COMBO_LINESET_VALUE = -1;
const NO_LINE_SET_VALUE = -2;
const NO_WIRE_VALUE = -3;

type BatchAccessoryChange = {
  accessory: AccessoryProductType;
  quantity: number;
};

type LineSetOptionProps = {
  condenser: SystemOptionCondenserType;
  airHandlerStartIndex: number;
  lineSetEquipment: EquipmentAccessoryData[];
  onAccessoryChange: (
    condenserUniqueId: string,
    accessory: AccessoryProductType,
    quantity: number,
    equipmentUniqueId?: string
  ) => void;
  onBatchAccessoryChange: (
    condenserUniqueId: string,
    changes: BatchAccessoryChange[],
    equipmentUniqueId?: string
  ) => void;
  includeRecommended: boolean;
};

export default function LineSetOption({
  condenser,
  airHandlerStartIndex,
  lineSetEquipment,
  onAccessoryChange,
  onBatchAccessoryChange,
  includeRecommended
}: LineSetOptionProps) {
  const [lineSetDropdownOverrides, setLineSetDropdownOverrides] = useState<
    Record<string, number>
  >({});
  const [wireDropdownOverrides, setWireDropdownOverrides] = useState<
    Record<string, number>
  >({});
  const [lineSetCheckedZones, setLineSetCheckedZones] = useState<
    Record<string, boolean>
  >({});
  const [wireCheckedZones, setWireCheckedZones] = useState<
    Record<string, boolean>
  >({});
  const getZoneEquipment = (zoneUniqueId: string) => {
    return lineSetEquipment.find((e) => e.equipmentUniqueId === zoneUniqueId);
  };

  const getLineSetProducts = (equipment: EquipmentAccessoryData) => {
    const seen = new Set<number>();
    return equipment.recommendations
      .map((r) => ({
        product: equipment.accessoryProducts.get(r.accessoryProductId),
        priority: r.recommendedPriority
      }))
      .filter(
        (
          p
        ): p is {
          product: AccessoryProductType & { shortName?: string };
          priority: number;
        } => p.product !== undefined && p.product.type === 'line_set'
      )
      .sort((a, b) => {
        const aPriority = a.priority === 0 ? Infinity : a.priority;
        const bPriority = b.priority === 0 ? Infinity : b.priority;
        return aPriority - bPriority;
      })
      .filter((p) => {
        if (seen.has(p.product.productId)) return false;
        seen.add(p.product.productId);
        return true;
      })
      .map((p) => p.product);
  };

  const getRecommendedLineSetCombo = (
    equipment: EquipmentAccessoryData
  ): (AccessoryProductType & { shortName?: string })[] | null => {
    const priority1Products = equipment.recommendations
      .filter((r) => r.recommendedPriority === 1)
      .map((r) => equipment.accessoryProducts.get(r.accessoryProductId))
      .filter(
        (p): p is AccessoryProductType & { shortName?: string } =>
          p !== undefined && p.type === 'line_set'
      );
    return priority1Products.length >= 2 ? priority1Products : null;
  };

  const getRecommendedLineSet = (equipment: EquipmentAccessoryData) => {
    return equipment.recommendations
      .filter((r) => r.recommendedPriority === 1)
      .map((r) => equipment.accessoryProducts.get(r.accessoryProductId))
      .find(
        (p): p is AccessoryProductType & { shortName?: string } =>
          p !== undefined && p.type === 'line_set'
      );
  };

  const getAddedLineSet = (equipment: EquipmentAccessoryData) => {
    const lineSetProducts = getLineSetProducts(equipment);
    return lineSetProducts.find((product) =>
      equipment.selectedAccessories.some(
        (acc) => acc.productId === product.productId && acc.quantity > 0
      )
    );
  };

  const getDisplayedLineSetId = (
    zoneUniqueId: string,
    equipment: EquipmentAccessoryData
  ) => {
    const combo = getRecommendedLineSetCombo(equipment);
    if (combo) {
      const allComboAdded = combo.every((product) =>
        equipment.selectedAccessories.some(
          (acc) => acc.productId === product.productId && acc.quantity > 0
        )
      );
      if (allComboAdded) return COMBO_LINESET_VALUE;
    }

    const addedLineSet = getAddedLineSet(equipment);
    if (addedLineSet) return addedLineSet.productId;

    if (lineSetDropdownOverrides[zoneUniqueId] !== undefined)
      return lineSetDropdownOverrides[zoneUniqueId];

    if (combo) return COMBO_LINESET_VALUE;

    const recommended = getRecommendedLineSet(equipment);
    return recommended?.productId;
  };

  const getDisplayedLineSetProduct = (
    zoneUniqueId: string,
    equipment: EquipmentAccessoryData
  ) => {
    const displayedId = getDisplayedLineSetId(zoneUniqueId, equipment);
    if (!displayedId) return undefined;
    const lineSetProducts = getLineSetProducts(equipment);
    return lineSetProducts.find((p) => p.productId === displayedId);
  };

  const getWireProducts = (equipment: EquipmentAccessoryData) => {
    const seen = new Set<number>();
    return equipment.recommendations
      .map((r) => ({
        product: equipment.accessoryProducts.get(r.accessoryProductId),
        priority: r.recommendedPriority
      }))
      .filter(
        (
          p
        ): p is {
          product: AccessoryProductType & { shortName?: string };
          priority: number;
        } => p.product !== undefined && p.product.type === 'wire'
      )
      .sort((a, b) => {
        const aPriority = a.priority === 0 ? Infinity : a.priority;
        const bPriority = b.priority === 0 ? Infinity : b.priority;
        return aPriority - bPriority;
      })
      .filter((p) => {
        if (seen.has(p.product.productId)) return false;
        seen.add(p.product.productId);
        return true;
      })
      .map((p) => p.product);
  };

  const getRecommendedWire = (equipment: EquipmentAccessoryData) => {
    const products = getWireProducts(equipment);
    return products.length > 0 ? products[0] : undefined;
  };

  const getAddedWire = (equipment: EquipmentAccessoryData) => {
    const wireProducts = getWireProducts(equipment);
    return wireProducts.find((product) =>
      equipment.selectedAccessories.some(
        (acc) => acc.productId === product.productId && acc.quantity > 0
      )
    );
  };

  const getDisplayedWireId = (
    zoneUniqueId: string,
    equipment: EquipmentAccessoryData
  ) => {
    const addedWire = getAddedWire(equipment);
    if (addedWire) return addedWire.productId;
    if (wireDropdownOverrides[zoneUniqueId])
      return wireDropdownOverrides[zoneUniqueId];
    const recommended = getRecommendedWire(equipment);
    return recommended?.productId;
  };

  const getDisplayedWireProduct = (
    zoneUniqueId: string,
    equipment: EquipmentAccessoryData
  ) => {
    const displayedId = getDisplayedWireId(zoneUniqueId, equipment);
    if (!displayedId) return undefined;
    const wireProducts = getWireProducts(equipment);
    return wireProducts.find((p) => p.productId === displayedId);
  };

  const parseLengthFt = (
    product?: AccessoryProductType & { shortName?: string }
  ): number | undefined => {
    if (!product) return undefined;
    const match = (product.shortName ?? product.name ?? '').match(
      /(\d+(?:\.\d+)?)/
    );
    return match ? Number(match[1]) : undefined;
  };

  // Length of a line set option by id (sum for combo)
  const getLineSetLengthById = (
    equipment: EquipmentAccessoryData,
    lineSetId: number
  ): number | undefined => {
    if (lineSetId === COMBO_LINESET_VALUE) {
      const combo = getRecommendedLineSetCombo(equipment);
      if (!combo) return undefined;
      const total = combo.reduce((sum, p) => sum + (parseLengthFt(p) ?? 0), 0);
      return total > 0 ? total : undefined;
    }
    return parseLengthFt(
      getLineSetProducts(equipment).find((p) => p.productId === lineSetId)
    );
  };

  const getLineSetSettingsById = (
    equipment: EquipmentAccessoryData,
    lineSetId: number
  ): LineSetSettings | undefined => {
    if (lineSetId === COMBO_LINESET_VALUE) {
      const combo = getRecommendedLineSetCombo(equipment);
      for (const product of combo ?? []) {
        const rec = equipment.recommendations.find(
          (r) => r.accessoryProductId === product.productId
        );
        if (rec?.lineSetSettings) return rec.lineSetSettings;
      }
      return undefined;
    }
    return equipment.recommendations.find(
      (r) => r.accessoryProductId === lineSetId
    )?.lineSetSettings;
  };

  // The wire that matches line set length + 5 ft, regardless of inclusion
  const getPlus5Wire = (
    equipment: EquipmentAccessoryData,
    lineSetId: number | undefined
  ) => {
    if (lineSetId === undefined) return undefined;
    const len = getLineSetLengthById(equipment, lineSetId);
    if (len === undefined) return undefined;
    return getWireProducts(equipment).find((p) => parseLengthFt(p) === len + 5);
  };

  // The wire that ships with a line set: length === lineSetLength + 5
  const getIncludedWireForLineSet = (
    equipment: EquipmentAccessoryData,
    lineSetId: number | undefined
  ) => {
    if (lineSetId === undefined) return undefined;
    const settings = getLineSetSettingsById(equipment, lineSetId);
    if (!settings?.msWireIncluded) return undefined;
    return getPlus5Wire(equipment, lineSetId);
  };

  const getIncludedWireForZone = (
    zoneUniqueId: string,
    equipment: EquipmentAccessoryData
  ) => {
    if (!lineSetCheckedZones[zoneUniqueId]) return undefined;
    return getIncludedWireForLineSet(
      equipment,
      getDisplayedLineSetId(zoneUniqueId, equipment)
    );
  };

  // Check if the currently displayed wire for a zone is the "included" wire
  // (i.e., the line set includes wire AND the displayed wire is the +5 ft wire)
  const isWireIncludedForZone = (
    zoneUniqueId: string,
    equipment: EquipmentAccessoryData
  ): boolean => {
    const includedWire = getIncludedWireForZone(zoneUniqueId, equipment);
    if (!includedWire) return false;
    const displayedWireId = getDisplayedWireId(zoneUniqueId, equipment);
    return displayedWireId === includedWire.productId;
  };

  // Infrastructure for drain tubing included check (for future use when
  // drain_hose products are added to the line set panel):
  // const isDrainTubingIncludedForZone = (zoneUniqueId, equipment) => {
  //   const id = getDisplayedLineSetId(zoneUniqueId, equipment);
  //   const settings = id ? getLineSetSettingsById(equipment, id) : undefined;
  //   return !!settings?.drainTubingIncluded;
  // };

  const includeRecommendedInitRef = useRef(true);

  useEffect(() => {
    if (includeRecommendedInitRef.current) {
      includeRecommendedInitRef.current = false;
      return;
    }

    if (includeRecommended) {
      const newLineSetChecked: Record<string, boolean> = {};
      const newWireChecked: Record<string, boolean> = {};

      condenser.zones.forEach((zone) => {
        const equipment = getZoneEquipment(zone.uniqueId);
        if (!equipment) return;

        const comboProducts = getRecommendedLineSetCombo(equipment);
        if (comboProducts) {
          newLineSetChecked[zone.uniqueId] = true;
          setLineSetDropdownOverrides((prev) => ({
            ...prev,
            [zone.uniqueId]: COMBO_LINESET_VALUE
          }));
        } else {
          const recommendedLineSet = getRecommendedLineSet(equipment);
          if (recommendedLineSet) {
            newLineSetChecked[zone.uniqueId] = true;
            setLineSetDropdownOverrides((prev) => ({
              ...prev,
              [zone.uniqueId]: recommendedLineSet.productId
            }));
          }
        }

        // Prefer the wire included with the line set (length + 5), falling back
        // to the recommended wire. If wire is included, the checkbox is checked
        // but it is not added to cart (handled by the parent via includeRecommended).
        const selectedLineSetId = comboProducts
          ? COMBO_LINESET_VALUE
          : getRecommendedLineSet(equipment)?.productId;
        const includedWire =
          selectedLineSetId !== undefined
            ? getIncludedWireForLineSet(equipment, selectedLineSetId)
            : undefined;
        const wireToShow = includedWire ?? getRecommendedWire(equipment);
        if (wireToShow) {
          newWireChecked[zone.uniqueId] = true;
          setWireDropdownOverrides((prev) => ({
            ...prev,
            [zone.uniqueId]: wireToShow.productId
          }));
        }
      });

      setLineSetCheckedZones(newLineSetChecked);
      setWireCheckedZones(newWireChecked);
    } else {
      setLineSetCheckedZones({});
      setWireCheckedZones({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeRecommended]);

  const userInteractedRef = useRef(false);

  useEffect(() => {
    if (userInteractedRef.current) return;
    if (lineSetEquipment.length === 0) return;

    const newLineSetChecked: Record<string, boolean> = {};
    const newWireChecked: Record<string, boolean> = {};

    condenser.zones.forEach((zone) => {
      const equipment = getZoneEquipment(zone.uniqueId);
      if (!equipment) return;

      const comboProducts = getRecommendedLineSetCombo(equipment);
      if (comboProducts) {
        const allComboSelected = comboProducts.every((p) =>
          equipment.selectedAccessories.some(
            (acc) => acc.productId === p.productId && acc.quantity > 0
          )
        );
        if (allComboSelected) {
          newLineSetChecked[zone.uniqueId] = true;
          setLineSetDropdownOverrides((prev) => ({
            ...prev,
            [zone.uniqueId]: COMBO_LINESET_VALUE
          }));
        }
      }

      if (!newLineSetChecked[zone.uniqueId]) {
        const lineSetProducts = getLineSetProducts(equipment);
        const matchedLineSet = lineSetProducts.find((p) =>
          equipment.selectedAccessories.some(
            (acc) => acc.productId === p.productId && acc.quantity > 0
          )
        );
        if (matchedLineSet) {
          newLineSetChecked[zone.uniqueId] = true;
          setLineSetDropdownOverrides((prev) => ({
            ...prev,
            [zone.uniqueId]: matchedLineSet.productId
          }));
        }
      }

      const wireProductList = getWireProducts(equipment);
      const matchedWire = wireProductList.find((p) =>
        equipment.selectedAccessories.some(
          (acc) => acc.productId === p.productId && acc.quantity > 0
        )
      );
      if (matchedWire) {
        newWireChecked[zone.uniqueId] = true;
        setWireDropdownOverrides((prev) => ({
          ...prev,
          [zone.uniqueId]: matchedWire.productId
        }));
      } else {
        // Check if wire is included with the currently selected/displayed line
        // set (length + 5). If so, auto-check the wire checkbox and show the
        // included wire even though it's not in the cart.
        const displayedLineSetId = newLineSetChecked[zone.uniqueId]
          ? getDisplayedLineSetId(zone.uniqueId, equipment)
          : undefined;
        const includedWire = getIncludedWireForLineSet(
          equipment,
          displayedLineSetId
        );
        if (includedWire) {
          newWireChecked[zone.uniqueId] = true;
          setWireDropdownOverrides((prev) => ({
            ...prev,
            [zone.uniqueId]: includedWire.productId
          }));
        }
      }
    });

    setLineSetCheckedZones(newLineSetChecked);
    setWireCheckedZones(newWireChecked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineSetEquipment]);

  const setLineSetSelected = (zoneUniqueId: string, checked: boolean) => {
    userInteractedRef.current = true;
    const equipment = getZoneEquipment(zoneUniqueId);
    if (!equipment) return;

    const displayedId = getDisplayedLineSetId(zoneUniqueId, equipment);
    const clearsIncludedWire =
      !checked && isWireIncludedForZone(zoneUniqueId, equipment);

    const newChecked = {
      ...lineSetCheckedZones,
      [zoneUniqueId]: checked
    };
    setLineSetCheckedZones(newChecked);
    if (clearsIncludedWire) {
      setWireCheckedZones((prev) => ({ ...prev, [zoneUniqueId]: false }));
    }

    if (displayedId === COMBO_LINESET_VALUE) {
      const comboProducts = getRecommendedLineSetCombo(equipment);
      if (comboProducts) {
        const changes: BatchAccessoryChange[] = comboProducts.map(
          (product) => ({
            accessory: product,
            quantity: checked ? 1 : 0
          })
        );
        onBatchAccessoryChange(condenser.uniqueId, changes, zoneUniqueId);
      }
    } else {
      const displayedProduct = getDisplayedLineSetProduct(
        zoneUniqueId,
        equipment
      );
      if (!displayedProduct) return;

      onAccessoryChange(
        condenser.uniqueId,
        displayedProduct,
        checked ? 1 : 0,
        zoneUniqueId
      );
    }
  };

  const handleLineSetDropdownChange = (
    zoneUniqueId: string,
    productId: string | number
  ) => {
    userInteractedRef.current = true;
    const equipment = getZoneEquipment(zoneUniqueId);
    if (!equipment) return;

    const numericId = Number(productId);
    if (numericId === NO_LINE_SET_VALUE) {
      setLineSetSelected(zoneUniqueId, false);
      return;
    }

    const isNewCombo = numericId === COMBO_LINESET_VALUE;
    const wasSelected = !!lineSetCheckedZones[zoneUniqueId];
    const oldDisplayedId = wasSelected
      ? getDisplayedLineSetId(zoneUniqueId, equipment)
      : undefined;
    const isOldCombo = oldDisplayedId === COMBO_LINESET_VALUE;

    setLineSetDropdownOverrides((prev) => ({
      ...prev,
      [zoneUniqueId]: numericId
    }));
    setLineSetCheckedZones((prev) => ({
      ...prev,
      [zoneUniqueId]: true
    }));

    const lineSetProducts = getLineSetProducts(equipment);
    const comboProducts = getRecommendedLineSetCombo(equipment);

    // Collect all line-set + wire cart mutations for this zone into a single
    // batch so they don't clobber each other (each separate dispatch reads its
    // own selectedSystems snapshot).
    const changes: BatchAccessoryChange[] = [];

    if ((isOldCombo || isNewCombo) && comboProducts) {
      if (isOldCombo) {
        comboProducts.forEach((product) => {
          changes.push({ accessory: product, quantity: 0 });
        });
      } else {
        const oldProduct = lineSetProducts.find(
          (p) => p.productId === oldDisplayedId
        );
        if (oldProduct) {
          changes.push({ accessory: oldProduct, quantity: 0 });
        }
      }

      if (isNewCombo) {
        comboProducts.forEach((product) => {
          changes.push({ accessory: product, quantity: 1 });
        });
      } else {
        const newProduct = lineSetProducts.find(
          (p) => p.productId === numericId
        );
        if (newProduct) {
          changes.push({ accessory: newProduct, quantity: 1 });
        }
      }
    } else {
      const oldProduct = lineSetProducts.find(
        (p) => p.productId === oldDisplayedId
      );
      const newProduct = lineSetProducts.find((p) => p.productId === numericId);

      if (
        oldProduct &&
        newProduct &&
        oldProduct.productId !== newProduct.productId
      ) {
        changes.push({ accessory: oldProduct, quantity: 0 });
        changes.push({ accessory: newProduct, quantity: 1 });
      } else if (oldProduct && !newProduct) {
        changes.push({ accessory: oldProduct, quantity: 0 });
      } else if (newProduct) {
        changes.push({ accessory: newProduct, quantity: 1 });
      }
    }

    // Always switch the wire to the new line set's length + 5 ft. Use the new id
    // directly since lineSetDropdownOverrides state isn't updated yet.
    const newPlus5Wire = getPlus5Wire(equipment, numericId);
    const newIncludesWire = !!getLineSetSettingsById(equipment, numericId)
      ?.msWireIncluded;
    const addedWire = getAddedWire(equipment);
    const wireChecked = wireCheckedZones[zoneUniqueId];

    if (newPlus5Wire) {
      setWireDropdownOverrides((prev) => ({
        ...prev,
        [zoneUniqueId]: newPlus5Wire.productId
      }));

      if (newIncludesWire) {
        // Included: show the +5 wire, keep it OUT of the cart, and keep it selected.
        if (addedWire) {
          changes.push({ accessory: addedWire, quantity: 0 });
        }
        setWireCheckedZones((prev) => ({ ...prev, [zoneUniqueId]: true }));
      } else if (wireChecked) {
        // Not included but wire is selected: put the +5 wire in the cart.
        if (addedWire && addedWire.productId !== newPlus5Wire.productId) {
          changes.push({ accessory: addedWire, quantity: 0 });
        }
        changes.push({ accessory: newPlus5Wire, quantity: 1 });
      }
      // Not included and wire unchecked: only update the displayed wire.
    }

    if (changes.length > 0) {
      onBatchAccessoryChange(condenser.uniqueId, changes, zoneUniqueId);
    }
  };

  const setWireSelected = (zoneUniqueId: string, checked: boolean) => {
    userInteractedRef.current = true;
    const equipment = getZoneEquipment(zoneUniqueId);
    if (!equipment) return;

    const displayedProduct = getDisplayedWireProduct(zoneUniqueId, equipment);
    if (!displayedProduct) return;

    const newChecked = {
      ...wireCheckedZones,
      [zoneUniqueId]: checked
    };
    setWireCheckedZones(newChecked);

    // If wire is included with the line set, don't add/remove from cart
    const wireIncluded = isWireIncludedForZone(zoneUniqueId, equipment);
    if (wireIncluded) return;

    onAccessoryChange(
      condenser.uniqueId,
      displayedProduct,
      checked ? 1 : 0,
      zoneUniqueId
    );
  };

  const handleWireDropdownChange = (
    zoneUniqueId: string,
    productId: string | number
  ) => {
    userInteractedRef.current = true;
    const equipment = getZoneEquipment(zoneUniqueId);
    if (!equipment) return;

    const numericId = Number(productId);
    if (numericId === NO_WIRE_VALUE) {
      setWireSelected(zoneUniqueId, false);
      return;
    }

    const wasSelected = !!wireCheckedZones[zoneUniqueId];
    const oldWasIncluded =
      wasSelected && isWireIncludedForZone(zoneUniqueId, equipment);

    setWireDropdownOverrides((prev) => ({
      ...prev,
      [zoneUniqueId]: numericId
    }));
    setWireCheckedZones((prev) => ({
      ...prev,
      [zoneUniqueId]: true
    }));

    const oldProduct = wasSelected
      ? getDisplayedWireProduct(zoneUniqueId, equipment)
      : undefined;
    const wireProducts = getWireProducts(equipment);
    const newProduct = wireProducts.find((p) => p.productId === numericId);

    // Check if the NEW selection will be the included wire (length + 5)
    const includedWire = getIncludedWireForZone(zoneUniqueId, equipment);
    const newIsIncluded =
      !!includedWire && numericId === includedWire.productId;

    if (
      oldProduct &&
      newProduct &&
      oldProduct.productId !== newProduct.productId
    ) {
      const changes: BatchAccessoryChange[] = [];

      // Remove old from cart ONLY if it was not included
      if (!oldWasIncluded) {
        changes.push({ accessory: oldProduct, quantity: 0 });
      }

      // Add new to cart ONLY if it is not included
      if (!newIsIncluded) {
        changes.push({ accessory: newProduct, quantity: 1 });
      }

      if (changes.length > 0) {
        onBatchAccessoryChange(condenser.uniqueId, changes, zoneUniqueId);
      }
    } else if (oldProduct && !newProduct) {
      if (!oldWasIncluded) {
        onAccessoryChange(condenser.uniqueId, oldProduct, 0, zoneUniqueId);
      }
    } else if (newProduct && !newIsIncluded) {
      onAccessoryChange(condenser.uniqueId, newProduct, 1, zoneUniqueId);
    }
  };

  return (
    <div className='grid w-full grid-cols-[minmax(0,1fr)_22rem_minmax(0,1fr)] divide-x divide-gray-200'>
      <div className='flex min-w-0 flex-col justify-center gap-3 p-5'>
        <div className='flex items-center gap-2'>
          <CpuChipIcon className='h-6 w-6' />
          <span className='mr-3 text-lg font-bold'>{condenser.name}</span>
        </div>
        <div className='flex items-center divide-x divide-gray-200'>
          <div className='flex items-center gap-4 pr-8'>
            <ProductPhoto product={condenser.selectedProduct} />
            <div className='flex flex-col gap-1'>
              <span className='text-xs font-semibold uppercase text-gray-600'>
                Series
              </span>
              <span className='whitespace-nowrap font-semibold'>
                {condenser.selectedProduct.series}
              </span>
            </div>
          </div>
          <div className='flex grow basis-0 flex-col gap-1 pl-8'>
            <span className='text-xs font-semibold uppercase text-gray-600'>
              Product
            </span>
            <ProductLink
              type='condenser'
              product={condenser.selectedProduct}
              fullName
              hoverInfo
            />
          </div>
        </div>
      </div>
      <div className='flex min-w-0 flex-col'>
        {condenser.zones.map((zone) => {
          const equipment = getZoneEquipment(zone.uniqueId);
          const lineSetProducts = equipment
            ? getLineSetProducts(equipment)
            : [];
          const comboProducts = equipment
            ? getRecommendedLineSetCombo(equipment)
            : null;
          const displayedLineSetId = equipment
            ? getDisplayedLineSetId(zone.uniqueId, equipment)
            : undefined;
          const lineSetAdded = lineSetCheckedZones[zone.uniqueId] ?? false;
          const wireProducts = equipment ? getWireProducts(equipment) : [];
          const displayedWireId = equipment
            ? getDisplayedWireId(zone.uniqueId, equipment)
            : undefined;
          const wireAdded = wireCheckedZones[zone.uniqueId] ?? false;
          const zoneIncludedWire = equipment
            ? getIncludedWireForZone(zone.uniqueId, equipment)
            : undefined;

          const getRecommendedPriority = (productId: number) => {
            const rec = equipment?.recommendations.find(
              (r) => r.accessoryProductId === productId
            );
            return rec?.recommendedPriority;
          };

          const isComboRecommended = comboProducts
            ? comboProducts.every(
                (p) => getRecommendedPriority(p.productId) === 1
              )
            : false;

          const lineSetOptions = [
            ...(comboProducts
              ? [
                  {
                    label: `${comboProducts
                      .map((p) => p.shortName ?? p.name)
                      .join(' + ')}${
                      isComboRecommended ? ' (Recommended)' : ''
                    }`,
                    value: COMBO_LINESET_VALUE,
                    lengthFeet: comboProducts.reduce(
                      (sum, comboProduct) =>
                        sum + (parseLengthFt(comboProduct) ?? 0),
                      0
                    )
                  }
                ]
              : []),
            ...lineSetProducts.map((p) => {
              const priority = getRecommendedPriority(p.productId);

              const isPartOfRecommendedCombo =
                isComboRecommended &&
                comboProducts?.some((cp) => cp.productId === p.productId);
              const isRecommended = priority === 1 && !isPartOfRecommendedCombo;
              return {
                label: `${p.shortName ?? p.name}${
                  isRecommended ? ' (Recommended)' : ''
                }`,
                value: p.productId,
                lengthFeet: parseLengthFt(p) ?? Number.POSITIVE_INFINITY
              };
            })
          ].sort((a, b) => a.lengthFeet - b.lengthFeet);

          return (
            <div
              key={zone.uniqueId}
              className='flex min-h-[150px] items-center'
            >
              <div className='min-w-6 flex-1 border-b-2 border-dashed border-gray-200' />
              <div className='grid shrink-0 grid-cols-[auto_12.5rem] items-center gap-x-2 gap-y-2'>
                <span className='whitespace-nowrap text-lg font-bold text-gray-800'>
                  Line Set
                </span>
                <Select
                  placeholder='Select line set'
                  options={[
                    ...lineSetOptions,
                    {
                      label: 'No line set needed',
                      value: NO_LINE_SET_VALUE
                    }
                  ]}
                  onChange={(value) =>
                    handleLineSetDropdownChange(zone.uniqueId, value)
                  }
                  value={lineSetAdded ? displayedLineSetId : NO_LINE_SET_VALUE}
                  preventDeselection
                />
                {wireProducts.length > 0 && (
                  <>
                    <span className='whitespace-nowrap text-lg font-bold text-gray-800'>
                      Wire
                    </span>
                    <Select
                      placeholder='Select wire'
                      options={[
                        ...[...wireProducts]
                          .sort(
                            (a, b) =>
                              (parseLengthFt(a) ?? Number.POSITIVE_INFINITY) -
                              (parseLengthFt(b) ?? Number.POSITIVE_INFINITY)
                          )
                          .map((p) => {
                            const wireRec = equipment?.recommendations.find(
                              (r) => r.accessoryProductId === p.productId
                            );
                            const isWireRecommended =
                              wireRec?.recommendedPriority === 1;
                            const isIncludedWire =
                              zoneIncludedWire?.productId === p.productId;
                            let suffix = '';
                            if (isIncludedWire) {
                              suffix = ' (Included with Line Set)';
                            } else if (isWireRecommended) {
                              suffix = ' (Recommended)';
                            }
                            return {
                              label: `${p.shortName ?? p.name}${suffix}`,
                              value: p.productId
                            };
                          }),
                        { label: 'No wire needed', value: NO_WIRE_VALUE }
                      ]}
                      onChange={(value) =>
                        handleWireDropdownChange(zone.uniqueId, value)
                      }
                      value={wireAdded ? displayedWireId : NO_WIRE_VALUE}
                      preventDeselection
                    />
                  </>
                )}
              </div>
              <div className='min-w-6 flex-1 border-b-2 border-dashed border-gray-200' />
            </div>
          );
        })}
      </div>
      <div className='flex min-w-0 flex-col'>
        {condenser.zones.map((zone, index) => (
          <div
            key={zone.uniqueId}
            className='flex min-h-[150px] grow flex-col justify-center gap-3 p-5'
          >
            <div className='flex flex-col gap-2'>
              <div className='flex items-center gap-2'>
                <RectangleStackIcon className='h-6 w-6 shrink-0' />
                <span className='text-lg font-bold'>
                  {zone.name} / Air Handler {airHandlerStartIndex + index + 1}
                </span>
              </div>
              {zone.zoneItems.length > 0 && (
                <div className='flex flex-wrap items-center gap-2'>
                  {zone.zoneItems.map((item) => (
                    <Tag key={item.uniqueId} color='default' text={item.name} />
                  ))}
                </div>
              )}
            </div>
            <div className='flex items-center divide-x divide-gray-200'>
              <div className='flex items-center gap-4 pr-8'>
                <ProductPhoto product={zone.selectedProduct} />
                <div className='flex flex-col gap-1'>
                  <span className='text-xs font-semibold uppercase text-gray-600'>
                    Type
                  </span>
                  <span className='font-semibold'>
                    {zone.selectedProduct.type ?? 'N/A'}
                  </span>
                </div>
              </div>
              <div className='flex grow basis-0 flex-col gap-1 pl-8'>
                <span className='text-xs font-semibold uppercase text-gray-600'>
                  Product
                </span>
                <ProductLink
                  type='air handler'
                  product={zone.selectedProduct}
                  fullName
                  hoverInfo
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
